import { useCallback, useMemo } from 'react';
import { Cart, LineItem } from 'shared/types/cart';
import { Discount } from 'shared/types/cart/Discount';
import { Order } from 'shared/types/cart/Order';
import { Product, Variant } from 'shared/types/product';
import useSWR, { mutate } from 'swr';
import useI18n from 'helpers/hooks/useI18n';
import useSdk from 'helpers/hooks/useSdk';
import { sendAddToCartEvent, sendRemoveFromCartEvent } from 'helpers/utils/ga4';
import mapCosts from 'helpers/utils/mapCosts';
import { revalidateOptions } from 'frontastic';
import { CartDetails, UseCartReturn } from './types';

const useCart = (): UseCartReturn => {
  const sdk = useSdk();
  const extensions = sdk.composableCommerce;

  const { currency } = useI18n();

  const result = useSWR('/action/cart/getCart', extensions.cart.getCart, {
    ...revalidateOptions,
    revalidateIfStale: true,
  });

  const shippingMethodsResults = useSWR(
    '/action/cart/getShippingMethods',
    extensions.cart.getShippingMethods,
    revalidateOptions,
  );

  const data = result.data?.isError ? {} : { data: result.data?.data as unknown as Cart };

  const shippingMethods = shippingMethodsResults.data?.isError ? {} : { data: shippingMethodsResults.data?.data };

  const totalItems = (data.data as Cart)?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !data?.data?.lineItems?.length;

  const isShippingAccurate = !!data?.data?.shippingInfo;

  const hasOutOfStockItems = !!data?.data?.lineItems?.some((lineItem) => !lineItem.variant?.isOnStock);

  const transaction = useMemo(() => mapCosts({ cart: data.data, currency }), [data.data, currency]);

  const addItem = useCallback(
    async (product: Pick<Product, 'name' | 'productId'>, variant: Variant, quantity: number) => {
      const payload = {
        variant: {
          sku: variant.sku,
          count: quantity,
        },
      };
      const res = await extensions.cart.addItem(payload);

      sendAddToCartEvent(product, variant, quantity);

      mutate('/action/cart/getCart', res);
    },
    [extensions],
  );

  const orderCart = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'cart/checkout' });
    mutate('/action/cart/getCart');

    return (res.isError ? {} : res.data) as Order;
  }, [sdk]);

  const queryOrder = useCallback(
    async (orderId: string) => {
      const res = await extensions.cart.queryOrders({ orderIds: [orderId] });
      mutate('/action/cart/getCart');

      return (res.isError ? {} : res.data.items?.[0]) as Order;
    },
    [extensions],
  );

  const queryOrderByCartId = useCallback(
    async (cartId: string) => {
      const res = await extensions.cart.queryOrders({ cartId });
      mutate('/action/cart/getCart');

      return (res.isError ? {} : res.data.items?.[0]) as Order;
    },
    [extensions],
  );

  const getOrder = useCallback(
    async (orderId: string) => {
      const res = await extensions.cart.getOrder({ orderId });
      mutate('/action/cart/getOrder');

      return res.isError ? null : res.data;
    },
    [extensions],
  );

  const orderHistory = useCallback(async () => {
    const res = await extensions.cart.queryOrders({ sortAttributes: [{ createdAt: 'desc' }] });

    return res.isError ? ([] as Order[]) : (res.data.items as Order[]);
  }, [extensions]);

  const getProjectSettings = useCallback(async () => {
    const res = await extensions.project.getSettings();

    return res.isError ? {} : res.data;
  }, [extensions]);

  const removeItem = useCallback(
    async (lineItem: LineItem) => {
      if (!lineItem?.variant || !lineItem.lineItemId) return;

      const payload = {
        lineItem: { id: lineItem.lineItemId },
      };

      const res = await extensions.cart.removeItem(payload);

      sendRemoveFromCartEvent(lineItem, lineItem.variant, lineItem.count || 0);

      mutate('/action/cart/getCart', res);
    },
    [extensions],
  );

  const updateItem = useCallback(
    async (lineItemId: string, newQuantity: number) => {
      const payload = {
        lineItem: {
          id: lineItemId,
          count: newQuantity,
        },
      };
      const res = await extensions.cart.updateItem(payload);
      mutate('/action/cart/getCart', res);
    },
    [extensions],
  );

  const updateCart = useCallback(
    async (payload: CartDetails): Promise<Cart> => {
      const res = await extensions.cart.updateCart(payload);

      mutate('/action/cart/getCart', res);

      return (res.isError ? {} : res.data) as Cart;
    },
    [extensions],
  );

  const setShippingMethod = useCallback(
    async (shippingMethodId: string) => {
      const payload = {
        shippingMethod: {
          id: shippingMethodId,
        },
      };

      const res = await extensions.cart.setShippingMethod(payload);

      mutate('/action/cart/getCart', res);
    },
    [extensions],
  );

  const redeemDiscountCode = useCallback(
    async (code: string) => {
      const payload = {
        code: code,
      };
      const res = await extensions.cart.redeemDiscountCode(payload);

      if (!res.isError && (res.data as Cart).cartId) {
        mutate('/action/cart/getCart', res);
      } else {
        throw new Error((res.isError ? res.error.message : '') ?? 'code not valid');
      }
    },
    [extensions],
  );

  const removeDiscountCode = useCallback(
    async (discount: Discount) => {
      const res = await extensions.cart.removeDiscountCode({ discountId: discount.discountId as string });

      mutate('/action/cart/getCart', res);
    },
    [extensions],
  );

  const resetCart = useCallback(async () => {
    await sdk.callAction({ actionName: 'cart/resetCart' });
  }, [sdk]);

  return {
    ...data,
    totalItems,
    isEmpty,
    isShippingAccurate,
    hasOutOfStockItems,
    transaction,
    addItem,
    updateCart,
    setShippingMethod,
    removeItem,
    updateItem,
    shippingMethods,
    orderCart,
    queryOrder,
    getOrder,
    orderHistory,
    getProjectSettings,
    redeemDiscountCode,
    removeDiscountCode,
    resetCart,
    queryOrderByCartId,
  };
};

export default useCart;
